// DispenserPerformanceReport.jsx
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../Redux/Actions/SnackbarActions/SnackbarActions";
import PharmacySnackbar from "../../Utils/Snackbars/SnackbarUtil";
import axios from "axios";
import config from "../../Helpers/config";
import { titleCase } from "../../Utils/titleCaseFunction";
import { formatDigits } from "../../Utils/formatAmount";
import ReusableDashboardHeader from "../../Containers/Dasboard/ReusableDashboardHeader";
import { Container } from "../../Utils/styledComponents";
import { BackButton } from "../../Utils/FormInputs/BackButton";
import { MuiPaper, EnhancedTableHead, EnhancedTableToolbar } from "../../Utils/Tables/CustomTable";
import { useExcelReports, usePdfReports } from "../Invoice/CustomHooks/useExcelReports";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

// Colors for pie chart
const COLORS = ['#11998e', '#38ef7d', '#667eea', '#764ba2', '#f093fb', '#f5576c', '#ff9a9e', '#a8edea', '#fed6e3', '#d299c2'];

const headData = [
  { id: "dispenser_name", label: "Dispenser Name", align: "left" },
  { id: "num_sales", label: "Sales Count", align: "center" },
  { id: "total_sales", label: "Total Sales (UGX)", align: "right" },
];

const detailHeadData = [
  { id: "invoice_number", label: "Invoice #", align: "left" },
  { id: "sale_date", label: "Date", align: "left" },
  { id: "customer_name", label: "Customer", align: "left" },
  { id: "product_name", label: "Product", align: "left" },
  { id: "quantity", label: "Qty", align: "center" },
  { id: "rate", label: "Rate (UGX)", align: "right" },
  { id: "total_price", label: "Total (UGX)", align: "right" },
];

const DispenserPerformanceReport = ({ actions, snackbars }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchId, setSearchId] = useState("");
  const [selectedDispenser, setSelectedDispenser] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const { open, message, variant } = snackbars;

  // Default: last 30 days
  useEffect(() => {
    const today = new Date();
    const last30 = new Date();
    last30.setDate(today.getDate() - 30);

    setEndDate(today.toISOString().split("T")[0]);
    setStartDate(last30.toISOString().split("T")[0]);
  }, []);

  const fetchReport = async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${config.epharmUrl}/Creport/dispenser_performance_report`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          user_id: searchId || undefined,
        },
      });

      if (res.data.success) {
        setReportData(res.data.data || []);
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load report";
      setError(msg);
      actions.snackbarActions.errorSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetails = async (dispenser_id) => {
    setDetailsLoading(true);
    try {
      const res = await axios.get(`${config.epharmUrl}/Creport/dispenser_sales_details/${dispenser_id}`, {
        params: { start_date: startDate, end_date: endDate },
      });

      if (res.data.success) {
        setDetailsData(res.data.data || []);
        const staff = reportData.find(r => r.dispenser_id === dispenser_id);
        setSelectedDispenser(staff?.dispenser_name || "Dispenser");
        setDetailsModal(true);
      }
    } catch (err) {
      actions.snackbarActions.errorSnackbar("Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) fetchReport();
  }, [startDate, endDate, searchId]);

  // Export
  const excelObj = {
    file_name: `Dispenser Performance (${startDate} to ${endDate})`,
    url: "Creport/dispenser_performance_report_excel",
    params: { start_date: startDate, end_date: endDate },
  };
  const pdfObj = {
    file_name: `Dispenser Performance (${startDate} to ${endDate})`,
    url: "Creport/dispenser_performance_report_pdf",
    params: { start_date: startDate, end_date: endDate },
  };
  const { exportExcel } = useExcelReports(actions, excelObj);
  const { exportPDF } = usePdfReports(actions, pdfObj);

  // Summary
  const totalSales = reportData.reduce((sum, r) => sum + r.total_sales, 0);
  const totalDispensers = reportData.length;
  const topDispenser = reportData[0]?.dispenser_name || "N/A";

  // Prepare data for charts
  const barChartData = reportData.map(item => ({
    name: item.dispenser_name.split(" ")[0], // First name for bar label
    sales: item.total_sales,
    count: item.num_sales,
  }));

  const pieChartData = reportData.map(item => ({
    name: item.dispenser_name.split(" ")[0],
    value: item.num_sales,
  }));

  return (
    <>
      <ReusableDashboardHeader
        component="Dispenser Performance Report"
        heading="Pharmacy"
        subHeading="Who dispensed the most? Real numbers only"
        link="/allreports"
      />

      <PharmacySnackbar open={open} message={message} variant={variant} handleCloseBar={() => actions.snackbarActions.hideSnackBar()} />

      <Container>
        <div className="mb-4">
          <BackButton text="All Reports" to="/allreports" />
        </div>

        {/* Filters */}
        <div className="row g-3 mb-4 align-items-end">
          <div className="col-md-3">
            <label className="form-label fw-bold">Start Date</label>
            <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold">End Date</label>
            <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-bold">Search by User ID (Optional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. 29"
              value={searchId}
              onChange={e => setSearchId(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <button onClick={fetchReport} className="btn btn-primary w-100">
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row mb-5">
          <div className="col-md-4">
            <div className="card border-0 shadow text-white" style={{ background: "linear-gradient(135deg, #11998e, #38ef7d)" }}>
              <div className="card-body text-center py-5">
                <h5>Total Sales by Dispensers</h5>
                <h1 className="display-5 fw-bold">UGX {formatDigits(totalSales)}</h1>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow text-white" style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }}>
              <div className="card-body text-center py-5">
                <h5>Active Dispensers</h5>
                <h1 className="display-5 fw-bold">{totalDispensers}</h1>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow text-white" style={{ background: "linear-gradient(135deg, #f093fb, #f5576c)" }}>
              <div className="card-body text-center py-5">
                <h5>Top Dispenser</h5>
                <h1 className="display-5 fw-bold">{topDispenser}</h1>
              </div>
            </div>
          </div>
        </div>

        {/* GRAPHS SECTION */}
        {reportData.length > 0 && (
          <div className="row mb-5">
            {/* Bar Chart - Sales Value */}
            <div className="col-lg-8 mb-4">
              <MuiPaper className="p-4 shadow">
                <h5 className="text-center mb-4 fw-bold text-primary">Sales Value by Dispenser</h5>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={barChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `UGX ${formatDigits(value)}`} />
                    <Tooltip formatter={(value) => `UGX ${formatDigits(value)}`} />
                    <Legend />
                    <Bar dataKey="sales" fill="#11998e" name="Total Sales" />
                  </BarChart>
                </ResponsiveContainer>
              </MuiPaper>
            </div>

            {/* Pie Chart - Sales Count */}
            <div className="col-lg-4 mb-4">
              <MuiPaper className="p-4 shadow">
                <h5 className="text-center mb-4 fw-bold text-primary">Sales Count Distribution</h5>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </MuiPaper>
            </div>
          </div>
        )}

        {/* Table */}
        <MuiPaper className="shadow">
          <EnhancedTableToolbar title="Dispenser Performance Report">
            <div className="d-flex gap-2">
              <button onClick={exportExcel} className="btn btn-success btn-sm">
                Export Excel
              </button>
              <button onClick={exportPDF} className="btn btn-danger btn-sm">
                Export PDF
              </button>
            </div>
          </EnhancedTableToolbar>

          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <EnhancedTableHead headCells={headData} />
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={3} className="text-center text-danger py-4">
                      {error}
                    </td>
                  </tr>
                ) : reportData.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-5">
                      No dispenser sales found
                    </td>
                  </tr>
                ) : (
                  reportData.map((row, i) => (
                    <tr
                      key={i}
                      className="cursor-pointer table-row-hover"
                      onClick={() => fetchDetails(row.dispenser_id)}
                    >
                      <td className="fw-bold text-primary">
                        {row.dispenser_name}
                      </td>
                      <td className="text-center fw-bold">{row.num_sales}</td>
                      <td className="text-end fw-bold text-success">
                        UGX {formatDigits(row.total_sales)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </MuiPaper>

        {/* Details Modal */}
        <Modal isOpen={detailsModal} toggle={() => setDetailsModal(false)} size="xl" centered>
          <ModalHeader toggle={() => setDetailsModal(false)} className="bg-primary text-white">
            Sales by {selectedDispenser}
          </ModalHeader>
          <ModalBody>
            {detailsLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : detailsData.length === 0 ? (
              <div className="text-center py-5 text-muted">
                No sales found
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <EnhancedTableHead headCells={detailHeadData} />
                  <tbody>
                    {detailsData.map((d, i) => (
                      <tr key={i}>
                        <td><span className="badge bg-light text-dark">{d.invoice_number}</span></td>
                        <td>{d.sale_date}</td>
                        <td>{d.customer_name || "Walk-in"}</td>
                        <td>{titleCase(d.product_name)}</td>
                        <td className="text-center">{d.quantity}</td>
                        <td className="text-end">{formatDigits(d.rate)}</td>
                        <td className="text-end fw-bold text-success">{formatDigits(d.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </>
  );
};

const mapStateToProps = state => ({ snackbars: state.snackbars });

const mapDispatchToProps = dispatch => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(mapStateToProps, mapDispatchToProps)(DispenserPerformanceReport);